<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
// icons
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import LogoWhiteHorizontal from '~/assets/svg/logo_white_horizontal.svg?component';

import links from '@/assets/json/footer.json';

/********************
 * PROPS & EMITS    *
 ********************/
export interface CiFooterProps {
  bgGray?: boolean;
}
const props = withDefaults(defineProps<CiFooterProps>(), {
  bgGray: false,
});

/********************
 * COMPOSITIONS      *
 ********************/
const localePath = useLocalePath();
const runtimeConfig = useRuntimeConfig();
const { $gettext } = useGettext();
const { locale } = useI18n();

/********************
 * REFS & VARS       *
 ********************/
const releaseVersion = ref(runtimeConfig.public.appVersion);
const selectedGroup = ref<number | null>(null);

/********************
 * FUNCTIONS         *
 ********************/
function onToggleAccordion(idx: number) {
  selectedGroup.value = selectedGroup.value === idx ? null : idx;
}

function getRouterLinkFor(path: Record<string, string>): RouteLocationRaw {
  return {
    name: 'info-all',
    params: { all: [path[locale.value] || path.en || path.de] },
  };
}

function getLink(name: string, fallbackToEnglish = true) {
  const link = links.footer[name];

  if (link && link[locale.value]) {
    return link[locale.value];
  }

  if (fallbackToEnglish && link && link.en) {
    return link.en;
  }

  if (link && link.civ1 && typeof link.civ1 === 'string') {
    return link.civ1.replace('$lang', locale.value);
  }

  return null;
}

function openCookieSettings() {
  // @ts-check
  window.openCookieSettings();
}

function scrollToTop(event: Event) {
  event.preventDefault();
  window.scrollTo({ top: 0, behavior: 'smooth' });
}
</script>

<template>
  <footer
    id="footer"
    class="overflow-hidden pt-28"
    :class="{ 'bg-gray-10': props.bgGray }"
  >
    <div
      class="relative z-10 bg-dark-green text-white before:absolute before:left-1/2 before:top-[-60px] before:-z-1 before:h-[160px] before:w-[150%] before:-translate-x-1/2 before:rounded-tl-[60%] before:rounded-tr-[50%] before:bg-primary before:content-['*'] after:absolute after:left-1/2 after:top-[-40px] after:-z-1 after:h-[160px] after:w-[150%] after:-translate-x-1/2 after:rounded-t-[50%] after:bg-dark-green after:content-['*'] md:before:w-[120%] md:after:w-[120%]"
    >
      <a
        href="#"
        class="group absolute left-1/2 top-[-80px] flex size-[50px] -translate-x-1/2 items-center justify-center rounded-full bg-warning"
        title="Zum Seitenanfang"
        :aria-label="$gettext('Back to top')"
        @click="scrollToTop"
      >
        <CiAwesomeIcon
          class="fill-white transition-transform duration-300 ease-linear group-hover:translate-y-[-3px]"
          :icon="faChevronUp"
          ratio="1"
        />
      </a>

      <section class="container grid grid-cols-12 !px-0 sm:!px-[15px] sm:!pt-4">
        <div class="col-span-12 pb-3 text-center sm:col-span-4 sm:pr-[15px] md:text-left lg:col-span-3">
          <nuxt-link :to="localePath('index')" aria-label="home">
            <LogoWhiteHorizontal class="inline-block w-3/5 sm:w-full md:w-[70%]" />
          </nuxt-link>
        </div>
        <div class="col-span-12 grid grid-cols-4 gap-x-[30px] !pt-1 sm:col-span-8 md:!pt-2 lg:col-span-9">
          <div class="col-span-4 sm:col-span-2 lg:col-span-1">
            <CiAccordion
              :show="selectedGroup === 1"
              :mq="'sm'"
              layout="primary"
              class="p-0"
              @accordion-toggle="onToggleAccordion(1)"
            >
              <template #header>
                <div class="py-3 font-bold sm:py-0 sm:pb-2">
                  {{ $gettext('For Traveller') }}
                </div>
              </template>
              <ul class="m-0 list-none p-0 pb-2 sm:pb-12">
                <li
                  v-if="getLink('fanclub')"
                  class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0"
                >
                  <nuxt-link
                    :to="localePath(getRouterLinkFor(links.footer.fanclub))"
                    class="block p-2 text-white hover:underline sm:px-0"
                  >
                    FANCLUB
                  </nuxt-link>
                </li>

                <li
                  v-if="getLink('camperApp')"
                  class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0"
                >
                  <nuxt-link
                    :to="localePath(getRouterLinkFor(links.footer.camperApp))"
                    class="block p-2 text-white hover:underline sm:px-0"
                  >
                    Camper App
                  </nuxt-link>
                </li>
                <li class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0">
                  <a
                    class="block p-2 text-white hover:underline sm:px-0"
                    :href="locale === 'de' ? 'https://www.camping.info/magazin/de': 'https://www.camping.info/magazin/en'"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ $gettext('Magazine') }}
                  </a>
                </li>
                <!--
                <li
                  v-if="getLink('newsletter')"
                  class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0"
                >
                  <a
                    :href="getLink('newsletter')"
                    class="block p-2 text-white hover:underline sm:px-0"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ $gettext('Newsletter') }}
                  </a>
                </li>
                -->
                <li
                  v-if="getLink('orderCampingGuide', false)"
                  class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0"
                >
                  <a
                    :href="getLink('orderCampingGuide')"
                    class="block p-2 text-white hover:underline sm:px-0"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ $gettext('Order Camping Guide') }}
                  </a>
                </li>
                <li class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0">
                  <nuxt-link
                    :to="localePath({ name: 'country' })"
                    class="block p-2 text-white hover:underline sm:px-0"
                  >
                    {{ $gettext('Popular countries') }}
                  </nuxt-link>
                </li>
                <li class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0">
                  <nuxt-link
                    :to="localePath({ name: 'popularRegion' })"
                    class="block p-2 text-white hover:underline sm:px-0"
                  >
                    {{ $gettext('Popular regions') }}
                  </nuxt-link>
                </li>
                <li class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0">
                  <nuxt-link
                    :to="localePath({ name: 'topic' })"
                    class="block p-2 text-white hover:underline sm:px-0"
                  >
                    {{ $gettext('Camping topics') }}
                  </nuxt-link>
                </li>
              </ul>
            </CiAccordion>
          </div>
          <div class="col-span-4 sm:col-span-2 lg:col-span-1">
            <CiAccordion
              :show="selectedGroup === 2"
              :mq="'sm'"
              layout="primary"
              class="p-0"
              @accordion-toggle="onToggleAccordion(2)"
            >
              <template #header>
                <div class="py-3 font-bold sm:py-0 sm:pb-2">
                  {{ $gettext('Advertising opportunities') }}
                </div>
              </template>
              <ul class="m-0 list-none p-0 pb-2 sm:pb-12">
                <li
                  v-if="getLink('forCampsites')"
                  class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0"
                >
                  <a
                    :href="getLink('forCampsites')"
                    class="block p-2 text-white hover:underline sm:px-0"
                  >
                    {{ $gettext('For Campsites') }}
                  </a>
                </li>
                <li
                  v-if="getLink('forDestinations')"
                  class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0"
                >
                  <a
                    :href="getLink('forDestinations')"
                    class="block p-2 text-white hover:underline sm:px-0"
                  >
                    {{ $gettext('For Destinations') }}
                  </a>
                </li>
                <li
                  v-if="getLink('forIndustryPartners')"
                  class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0"
                >
                  <a
                    :href="getLink('forIndustryPartners')"
                    class="block p-2 text-white hover:underline sm:px-0"
                  >
                    {{ $gettext('For Industry Partners') }}
                  </a>
                </li>
                <li
                  v-if="getLink('affiliateProgram')"
                  class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0"
                >
                  <a
                    :href="getLink('affiliateProgram')"
                    class="block p-2 text-white hover:underline sm:px-0"
                  >
                    {{ $gettext('Affiliate Program') }}
                  </a>
                </li>
                <li
                  v-if="getLink('ownerLogIn')"
                  class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0"
                >
                  <a
                    :href="getLink('ownerLogIn')"
                    class="block p-2 text-white hover:underline sm:px-0"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ $gettext('Owner Login') }}
                  </a>
                </li>
              </ul>
            </CiAccordion>
          </div>
          <div class="col-span-4 sm:col-span-2 lg:col-span-1">
            <CiAccordion
              :show="selectedGroup === 3"
              :mq="'sm'"
              layout="primary"
              class="p-0"
              @accordion-toggle="onToggleAccordion(3)"
            >
              <template #header>
                <div class="py-3 font-bold sm:py-0 sm:pb-2">
                  {{ $gettext('Support') }}
                </div>
              </template>
              <ul class="m-0 list-none p-0 pb-2 sm:pb-12">
                <li class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0">
                  <nuxt-link
                    :to="localePath(getRouterLinkFor(links.footer.faq))"
                    class="block p-2 text-white hover:underline sm:px-0"
                  >
                    {{ $gettext('FAQ') }}
                  </nuxt-link>
                </li>
                <li class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0">
                  <nuxt-link
                    :to="localePath(getRouterLinkFor(links.footer.contact))"
                    class="block p-2 text-white hover:underline sm:px-0"
                  >
                    {{ $gettext('Contact') }}
                  </nuxt-link>
                </li>
              </ul>
            </CiAccordion>
          </div>
          <div class="col-span-4 sm:col-span-2 lg:col-span-1">
            <CiAccordion
              :show="selectedGroup === 4"
              :mq="'sm'"
              layout="primary"
              class="p-0"
              @accordion-toggle="onToggleAccordion(4)"
            >
              <template #header>
                <div class="py-3 font-bold sm:py-0 sm:pb-2">
                  {{ $gettext('More about Camping.info') }}
                </div>
              </template>
              <ul class="m-0 list-none p-0 pb-2 sm:pb-12">
                <li
                  v-if="getLink('aboutUs')"
                  class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0"
                >
                  <a
                    :href="getLink('aboutUs')"
                    class="block p-2 text-white hover:underline sm:px-0"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ $gettext('About us') }}
                  </a>
                </li>
                <li
                  v-if="getLink('career')"
                  class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0"
                >
                  <nuxt-link
                    :to="localePath(getRouterLinkFor(links.footer.career))"
                    class="block p-2 text-white hover:underline sm:px-0"
                  >
                    {{ $gettext('Career and jobs') }}
                  </nuxt-link>
                </li>
                <li
                  v-if="getLink('press')"
                  class="footer__list-item mx-3 py-2 sm:mx-0 sm:py-0"
                >
                  <a
                    :href="getLink('press')"
                    class="block p-2 text-white hover:underline sm:px-0"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ $gettext('Press') }}
                  </a>
                </li>
              </ul>
            </CiAccordion>
          </div>
        </div>
      </section>

      <hr class="mb-0 mt-2 hidden border-2 border-black/10 sm:block" />

      <section class="container grid grid-cols-2 gap-x-[30px] py-2 !pb-6">
        <div class="col-span-2 !pb-2 lg:col-span-1">
          <div class="py-6 text-center md:text-left">
            {{ $gettext('Follow us on social media') }}
          </div>
          <div class="flex w-full justify-between lg:w-[90%]">
            <div class="footer-apps__link inline-block text-center">
              <a
                href="https://www.facebook.com/Camping.info/"
                target="_blank"
                rel="noopener"
              >
                <img
                  loading="lazy"
                  src="~/assets/svg/icon-facebook.svg"
                  alt="facebook"
                  width="50"
                  height="50"
                />
              </a>
            </div>
            <div class="footer-apps__link inline-block text-center">
              <a
                href="https://www.instagram.com/camping.info/"
                target="_blank"
                rel="noopener"
              >
                <img
                  loading="lazy"
                  src="~/assets/svg/icon-instagram.svg"
                  alt="instagram"
                  width="50"
                  height="50"
                />
              </a>
            </div>
            <div class="footer-apps__link inline-block text-center">
              <a
                href="https://www.youtube.com/channel/UCJ9JdmK7fuPYl0dPDwbpkrg"
                target="_blank"
                rel="noopener"
              >
                <img
                  loading="lazy"
                  src="~/assets/svg/icon-youtube.svg"
                  alt="youtube"
                  width="50"
                  height="50"
                />
              </a>
            </div>
            <div class="footer-apps__link inline-block text-center">
              <a
                href="https://www.tiktok.com/@camping.info"
                target="_blank"
                rel="noopener"
              >
                <img
                  loading="lazy"
                  src="~/assets/svg/icon-tiktok.svg"
                  alt="tiktok"
                  width="50"
                  height="50"
                />
              </a>
            </div>
          </div>
        </div>
        <div class="col-span-2 grid grid-cols-3 gap-x-[30px] lg:col-span-1">
          <div class="col-span-3 text-center sm:col-span-1 sm:text-left">
            <div class="py-6">
              {{ $gettext('Our Magazine') }}
            </div>
            <a
              target="_blank"
              class="inline-block w-1/2 max-w-[130px] sm:w-full"
              :href="locale === 'de' ? 'https://www.camping.info/magazin/de': 'https://www.camping.info/magazin/en'"
              aria-label="Magazine"
              rel="noopener"
            >
              <svg
                width="100%"
                viewBox="0 0 208 69"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                style="max-width: 200px"
              >
                <g
                  stroke="none"
                  stroke-width="1"
                  fill="none"
                  fill-rule="evenodd"
                >
                  <g transform="translate(-84.000000, -1585.000000)">
                    <g transform="translate(-1.000000, 839.000000)">
                      <g transform="translate(16.000000, 702.000000)">
                        <g transform="translate(69.000000, 44.000000)">
                          <g fill-rule="nonzero">
                            <g fill="#54C100">
                              <path
                                d="M190.900279,1.35525272e-19 L16.5267796,1.35525272e-19 C15.8911662,1.35525272e-19 15.2631795,1.35525272e-19 14.6292475,0.00344500019 C14.0985874,0.00689000038 13.5721567,0.0168977259 13.0364007,0.0253207514 C11.872491,0.0389316089 10.7113185,0.140687365 9.56297386,0.329703743 C8.4162422,0.52283498 7.30543276,0.886942411 6.26811501,1.4097113 C5.23206593,1.93690263 4.28539827,2.62193371 3.46245486,3.43995326 C2.63519114,4.25567246 1.94560295,5.1985335 1.42020674,6.23228094 C0.893360468,7.26397086 0.528043559,8.36952167 0.336873352,9.51074982 C0.143885639,10.6505366 0.0400371953,11.8034673 0.0262600014,12.959195 C0.0101746672,13.4873135 0.00847600047,14.017189 0,14.5453765 L0,54.3596915 C0.00847600047,54.8945278 0.0101746672,55.4126558 0.0262600014,55.9476643 C0.0400414201,57.1033347 0.143889866,58.2562079 0.336873352,59.3959373 C0.527515681,60.5378326 0.89285419,61.644018 1.42020674,62.6760942 C1.94536439,63.7064987 2.63504955,64.6455426 3.46245486,65.4567261 C4.28227793,66.2783828 5.2295575,66.96388 6.26811501,67.487037 C7.30542489,68.011205 8.41611457,68.3774797 9.56297386,68.57359 C10.7115137,68.7610923 11.8725668,68.8628894 13.0364007,68.878128 C13.5721567,68.889841 14.0985874,68.8965588 14.6292475,68.8965588 C15.2631622,68.9000038 15.8912009,68.9000038 16.5267796,68.9000038 L190.900279,68.9000038 C191.523239,68.9000038 192.156252,68.9000038 192.779212,68.8965588 C193.307359,68.8965588 193.849025,68.889841 194.377172,68.878128 C195.538806,68.8636999 196.697673,68.7618963 197.843839,68.57359 C198.994398,68.3760991 200.10907,68.0099215 201.151386,67.487037 C202.189007,66.9635771 203.135412,66.2780987 203.954533,65.4567261 C204.779803,64.6423656 205.470961,63.7040086 206.002639,62.6760942 C206.526035,61.6432672 206.887896,60.5371702 207.075746,59.3959373 C207.269038,58.2560515 207.376572,57.1034348 207.397453,55.9476643 C207.404213,55.4126558 207.404213,54.8945278 207.404213,54.3596915 C207.417733,53.7339072 207.417733,53.1115679 207.417733,52.4756209 L207.417733,16.4259848 C207.417733,15.7951881 207.417733,15.1694382 207.404213,14.5453765 C207.404213,14.017189 207.404213,13.4873135 207.397453,12.9591261 C207.376576,11.8032983 207.269043,10.650624 207.075746,9.51068092 C206.887372,8.37011301 206.525532,7.26464979 206.002639,6.23221204 C204.932695,4.16017548 203.236072,2.47355388 201.151386,1.40955628 C200.109066,0.888064861 198.994276,0.524054135 197.843839,0.329548718 C196.697871,0.139699552 195.538883,0.0379083771 194.377172,0.0250796014 C193.849025,0.0166738009 193.307359,0.00657995036 192.779212,0.00322107518 C192.156252,-0.000223925012 191.523239,-0.000223925012 190.900279,-0.000223925012 L190.900279,1.35525272e-19 Z"
                              />
                              <path
                                d="M14.6377061,67.3928162 C14.1095941,67.3928162 13.5942394,67.3860985 13.0702701,67.3743855 C11.9848251,67.3603311 10.901917,67.2664278 9.83042721,67.0934457 C8.83131701,66.9224499 7.86344779,66.6043667 6.95874438,66.1496879 C6.06233662,65.6987764 5.24474068,65.1075286 4.53727758,64.3985943 C3.81957411,63.6980109 3.22205318,62.8854537 2.76839349,61.9931229 C2.30973484,61.0949927 1.99231577,60.1323053 1.82719343,59.1385958 C1.64887565,58.0708436 1.55239833,56.9911777 1.53859342,55.9089081 C1.52760408,55.5456328 1.51321742,54.3360932 1.51321742,54.3360932 L1.51321742,14.5453765 C1.51321742,14.5453765 1.52854008,13.3544227 1.53868008,13.0045312 C1.55189818,11.923976 1.64781448,10.8459856 1.82559877,9.77989046 C1.9910259,8.78345875 2.3086907,7.81799535 2.76759615,6.91692306 C3.21958854,6.02520861 3.81379215,5.21204464 4.52717225,4.50895405 C5.23975052,3.7991831 6.05996179,3.20489077 6.95789505,2.74774938 C7.86051919,2.29453136 8.82655926,1.97869192 9.82364987,1.81081267 C10.8986586,1.63609284 11.9853261,1.54163084 13.0745341,1.52821931 L14.6385555,1.50718758 L192.770719,1.50718758 L194.353425,1.52906333 C195.432726,1.54180845 196.509522,1.63542773 197.574652,1.80912462 C198.581837,1.97910966 199.557944,2.29715288 200.470879,2.7527963 C202.269765,3.67406867 203.733483,5.13160846 204.656879,6.92112596 C205.10846,7.81597778 205.421225,8.77356587 205.584559,9.76139081 C205.764461,10.8362332 205.865146,11.9226949 205.885813,13.0121102 C205.890839,13.4999222 205.890839,14.0239068 205.890839,14.5453765 C205.904533,15.191314 205.904533,15.8061259 205.904533,16.4259848 L205.904533,52.4756209 C205.904533,53.1014052 205.904533,53.7120315 205.890839,54.327653 C205.890839,54.88781 205.890839,55.4009428 205.884079,55.9290613 C205.86386,56.9990365 205.764853,58.0661056 205.587853,59.1217153 C205.426115,60.1225931 205.110424,61.0927947 204.651853,61.9982904 C204.194986,62.8809359 203.600896,63.6864545 202.891479,64.3851588 C202.18338,65.0979029 201.364341,65.6926099 200.465852,66.1464151 C199.555447,66.6045893 198.580776,66.9238488 197.574652,67.0934457 C196.503243,67.267351 195.420231,67.3612603 194.334705,67.3743855 C193.827012,67.3860985 193.295399,67.3928162 192.779212,67.3928162 L190.900279,67.3962612 L14.6377061,67.3928162 Z"
                              />
                            </g>
                            <g
                              transform="translate(14.300001, 16.380001)"
                              fill="#FFFFFF"
                            >
                              <g>
                                <path
                                  d="M0,30.0654562 C0,30.8813314 0.661397565,31.542729 1.47727281,31.542729 L10.3409097,31.542729 C12.3536998,31.5526232 14.1070488,32.9176873 14.6102281,34.8665928 C14.7788769,35.5197712 15.3685842,35.9757305 16.0431827,35.9745474 L16.4568191,35.9745474 C17.1314175,35.9757305 17.7211249,35.5197712 17.8897737,34.8665928 C18.392953,32.9176873 20.146302,31.5526232 22.1590921,31.542729 L31.022729,31.542729 C31.8386042,31.542729 32.5000018,30.8813314 32.5000018,30.0654562 L32.5000018,7.90636407 C32.5000018,7.09048883 31.8386042,6.42909126 31.022729,6.42909126 L22.1590921,6.42909126 C20.5600334,6.42370417 19.0032884,6.94261917 17.7272737,7.90636407 L17.7272737,7.90636407 C17.7272737,5.94737853 16.9490697,4.06862793 15.5638578,2.68341597 C14.1786458,1.29820401 12.2998952,0.520000029 10.3409097,0.520000029 L8.86363685,0.520000029 C8.04776161,0.520000029 7.38636404,1.18139759 7.38636404,1.99727284 L7.38636404,6.42909126 L1.47727281,6.42909126 C0.661397565,6.42909126 0,7.09048883 0,7.90636407 L0,30.0654562 Z M22.1590921,9.38363688 L29.5454562,9.38363688 L29.5454562,28.5881834 L22.1590921,28.5881834 C20.5602081,28.5838834 19.0038009,29.1026858 17.7272737,30.0654562 L17.7272737,13.8154553 C17.7272737,11.3678296 19.7114664,9.38363688 22.1590921,9.38363688 Z M10.3409097,3.47454565 C12.7885354,3.47454565 14.7727281,5.45873834 14.7727281,7.90636407 L14.7727281,24.156365 C13.4967134,23.1926201 11.9399684,22.6737051 10.3409097,22.6790922 L10.3409097,3.47454565 Z M2.95454562,9.38363688 L7.38636404,9.38363688 L7.38636404,24.156365 C7.38636404,24.9722402 8.04776161,25.6336378 8.86363685,25.6336378 L10.3409097,25.6336378 C12.7885354,25.6336378 14.7727281,27.6178305 14.7727281,30.0654562 L14.7727281,30.0654562 C13.4962009,29.1026858 11.9397937,28.5838834 10.3409097,28.5881834 L2.95454562,28.5881834 L2.95454562,9.38363688 Z"
                                />
                              </g>
                            </g>
                          </g>
                          <text
                            id="Jetzt-lesen"
                            font-size="13.0000007"
                            font-weight="normal"
                            line-spacing="18.200001"
                            fill="#FFFFFF"
                          >
                            <tspan
                              x="58.5000032"
                              y="22.0000007"
                            >READ NOW</tspan>
                          </text>
                          <text
                            id="CI-Magazin"
                            font-size="20.8000011"
                            font-weight="bold"
                            line-spacing="18.200001"
                            fill="#FFFFFF"
                          >
                            <tspan
                              x="58.5000032"
                              y="50.1000019"
                            >CI MAGAZIN</tspan>
                          </text>
                        </g>
                      </g>
                    </g>
                  </g>
                </g>
              </svg>
            </a>
          </div>
          <div class="col-span-3 text-center sm:col-span-2 sm:text-left">
            <div class="py-6">
              {{ $gettext('Our apps for Android and iOS') }}
            </div>
            <div class="flex flex-col items-center sm:flex-row">
              <a
                target="_blank"
                class="footer__app-store-link mr-0 w-1/2 max-w-[120px] sm:mr-3 sm:w-full"
                href="https://apps.apple.com/de/app/camping-info-2020-europa/id1188034434?itsct=apps_box_badge&amp;itscg=30200"
                title="Laden im AppStore"
                rel="noopener"
              >
                <img
                  class="h-auto w-full"
                  src="~/assets/svg/apple-app.svg"
                  alt="Laden im AppStore"
                  style="max-width: 200px"
                  width="120"
                  loading="lazy"
                />
              </a>
              <a
                target="_blank"
                rel="noopener"
                class="footer__app-store-link mt-2 max-w-[120px] sm:mr-3 sm:mt-0 sm:w-full"
                href="https://play.google.com/store/apps/details?id=com.camparound.camperapp.android&pcampaignid=MKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1"
                title="Jetzt bei Google Play"
              >
                <img
                  class="h-auto w-full"
                  src="~/assets/svg/google-app.svg"
                  alt="Jetzt bei Google Play"
                  style="max-width: 200px"
                  loading="lazy"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section class="bg-black/20 py-4 text-center text-xs uppercase leading-5">
        <ul class="m-0 flex list-none flex-wrap justify-center p-0 pb-2">
          <li class="inline">
            <nuxt-link
              :to="localePath(getRouterLinkFor(links.termsOfUse))"
              class="p-2 text-white hover:underline"
            >
              {{ $gettext('Terms of use') }}
            </nuxt-link>
          </li>

          <li class="inline">
            <nuxt-link
              :to="localePath(getRouterLinkFor(links.footer.imprint))"
              class="p-2 text-white hover:underline"
            >
              {{ $gettext('Imprint') }}
            </nuxt-link>
          </li>
          <li class="inline">
            <nuxt-link
              :to="localePath(getRouterLinkFor(links.footer.privacy))"
              class="p-2 text-white hover:underline"
            >
              {{ $gettext('Privacy') }}
            </nuxt-link>
          </li>
          <li class="inline">
            <a
              href="#"
              class="p-2 text-white hover:underline"
              @click.prevent="openCookieSettings"
            >
              {{ $gettext('Cookie settings') }}
            </a>
          </li>
          <li class="inline">
            <nuxt-link
              :to="localePath(getRouterLinkFor(links.footer.termsOfService))"
              class="p-2 text-white hover:underline"
            >
              {{ $gettext('Terms of Service') }}
            </nuxt-link>
          </li>
          <li class="inline">
            <span class="whitespace-nowrap p-2">&#x24B8;&nbsp;Camping.info</span>
          </li>
        </ul>
        <ul class="m-0 list-none p-0 lowercase">
          <li class="small inline">
            {{ releaseVersion }}
          </li>
        </ul>
      </section>
    </div>
  </footer>
</template>

<style></style>
